import React, { MutableRefObject } from 'react';
import { useDebounceEffect } from '../../../util/useDebounceEffect';
import { ACTION_FIELD_NAMES } from '../constants';
import type { ThirdPartyMode } from '../types';

type PersistWithBlob = (
  merged: Record<string, unknown>,
  provider: string,
  mode: ThirdPartyMode,
  payload: Record<string, unknown>,
) => void;

export function useThirdPartyFieldDebounce(
  isChangingDirectly: boolean,
  whoIsChanging: { name: string; value: string },
  setIsChangingDirectly: (v: boolean) => void,
  setWhoIsChanging: (v: { name: string; value: string }) => void,
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>,
  dataRef: MutableRefObject<Record<string, unknown>>,
  formValuesRef: MutableRefObject<Record<string, unknown>>,
  provider: string,
  mode: ThirdPartyMode,
  persistWithBlob: PersistWithBlob,
  removeConnectedEdges: () => Promise<void>,
  removeEdgesForField: (fieldName: string) => Promise<void>,
  userEditedFieldsRef: MutableRefObject<Set<string>>,
) {
  useDebounceEffect(
    () => {
      if (isChangingDirectly && whoIsChanging.name) {
        const fieldName = whoIsChanging.name;
        const value = whoIsChanging.value;
        const dataCur = dataRef.current;
        const formValuesCur = formValuesRef.current;
        setIsChangingDirectly(false);
        setWhoIsChanging({ name: '', value: '' });
        userEditedFieldsRef.current.add(fieldName);
        const richFormattingOff = value === 'false' || value === '';
        if (fieldName === 'richFormatting' && richFormattingOff) {
          setFormValues(prev => ({
            ...prev,
            blockKitJson: '',
            blockKitJsonData: [],
          }));
          const payload = {
            richFormatting: false,
            blockKitJson: '',
            blockKitJsonData: [],
          };
          const merged = { ...dataCur, ...formValuesCur, ...payload };
          removeEdgesForField('blockKitJson').then(() =>
            persistWithBlob(merged, provider, mode, payload),
          );
        } else if (
          (ACTION_FIELD_NAMES as readonly string[]).includes(fieldName)
        ) {
          const payload = { [fieldName]: value };
          const merged = { ...dataCur, ...formValuesCur, ...payload };
          removeConnectedEdges().then(() =>
            persistWithBlob(merged, provider, mode, payload),
          );
        } else {
          const payload = { [fieldName]: value };
          const merged = { ...dataCur, ...formValuesCur, ...payload };
          persistWithBlob(merged, provider, mode, payload);
        }
      }
    },
    [
      isChangingDirectly,
      whoIsChanging,
      persistWithBlob,
      removeConnectedEdges,
      removeEdgesForField,
      provider,
      mode,
      userEditedFieldsRef,
    ],
    300,
  );
}
